import { Icon, ICONS } from '@creditsights/styleguide-web';
import { ColDef } from 'ag-grid-community';
import { NavLink } from 'react-router-dom';
import { EM_DASH } from '../../Base.constants';
import { IssuerListItemDto } from '../../services/dtos/issuer/issuerList.dto';
import { dateFormatter, getCompanyPageLink, impliedValueGetter } from './Company.logic';

import Coverage from './Coverage';

export const additionalDefaultColDef: ColDef<IssuerListItemDto> = {
  comparator: () => 0, // Prevent sort trigger before data loads when using client side data model
};
export const COLUMN_PERSISTENCE_KEY = 'companies-table-columns';

const WEIGHTING_ICONS_MAP = {
  buy: ICONS.Buy,
  hold: ICONS.Hold,
  marketperform: ICONS.Marketperform,
  norecommendation: ICONS.Norecommendation,
  outperform: ICONS.Outperform,
  sell: ICONS.Sell,
  underperform: ICONS.Underperform,
};

export const COMPANY_COLUMNS: ColDef<IssuerListItemDto>[] = [
  {
    valueFormatter: () => '',
    colId: 'selection',
    lockPosition: 'left',
    pinned: 'left',
    checkboxSelection: true,
    resizable: false,
    suppressMovable: true,
    maxWidth: 25,
    headerCheckboxSelection: true,
    headerClass: 'selection-cell',
    cellClass: 'selection-cell',
    sortable: false,
  },
  {
    colId: 'name',
    field: 'displayName',
    headerName: 'Issuer/Borrower',
    pinned: 'left',
    cellRenderer: (params) => <NavLink to={getCompanyPageLink(params.data.tagId)}>{params.data.displayName}</NavLink>,
  },
  {
    colId: 'latestRecommendation.weighting',
    headerName: 'CS View',
    valueGetter: (params) => (params.data?.csCoverage ? params.data?.latestRec?.weighting?.toLowerCase() : null),
    cellRenderer: (params) =>
      params.value ? <Icon name={WEIGHTING_ICONS_MAP[params.value]} height={20} width={20} /> : EM_DASH,
    minWidth: 120,
    width: 120,
  },
  {
    colId: 'ticker',
    field: 'displayTicker',
    headerName: 'Ticker',
  },
  {
    colId: 'latestRecommendation.changedDate',
    field: 'latestRec.changedDate',
    headerName: 'Rec Changed',
    valueFormatter: dateFormatter,
  },
  {
    colId: 'latestRecommendation.publishDate',
    field: 'latestRec.publishDate',
    headerName: 'Rec Reviewed',
    valueFormatter: dateFormatter,
  },
  {
    colId: 'sector',
    headerName: 'Sector/Subsector',
    valueGetter: impliedValueGetter('sector'),
  },
  {
    colId: 'currency',
    headerName: 'Currency',
    valueGetter: impliedValueGetter('currency'),
    sortable: false,
  },
  {
    colId: 'creditQualities',
    headerName: 'Credit',
    minWidth: 150,
    valueGetter: (params) =>
      params?.data?.implied
        ?.map((tag) => {
          switch (tag.tagId) {
            case 'tag/high_yield':
              return 'High Yield';
            case 'tag/special_situations':
            case 'tag/investment_grade':
              return tag.displayName;
            default:
              return null;
          }
        })
        .filter(Boolean),
    valueFormatter: (params) => (params.value && params.value.length ? params.value.join(', ') : EM_DASH),
  },
  {
    colId: 'topLevelRegion',
    headerName: 'Region',
    valueGetter: impliedValueGetter('region'),
  },
  {
    colId: 'country',
    headerName: 'Country',
    valueGetter: impliedValueGetter('country'),
    sortable: false,
  },
  {
    colId: 'coverage',
    headerName: 'Coverage',
    cellRenderer: (params) => (
      <Coverage
        csCoverage={params.data?.csCoverage}
        crCoverage={params.data?.crCoverage}
        lfiCoverage={params.data?.lfiCoverage}
      />
    ),
    sortable: false,
  },
];
